import * as THREE from 'three';
import { HERBS, isWall, pieceScale, plotKey, plotView, RESOURCES, type HerbId, type GameMap, type MapObject, type PlotState, type ResourceKind } from '@sunderfen/shared';
import { BUILDERS, G, addMesh, freeTree, flames, mat, mergeStatic, within } from './models.js';
import { buildTerrain, hash2, tileColors, type Occluder } from './terrain.js';
import { Water } from './water.js';
import { Sky } from './sky.js';
import { PUSHERS, addWind, buildFoliage, treeDensity, wind, type FoliageSet } from './foliage.js';
import { Butterflies, GroundBirds, MistBanks, type Walker } from './critters.js';
import { Wildlife } from './wildlife.js';
import { Particles } from './particles.js';
import { Glows } from './glows.js';
import { Birds } from './birds.js';
import { PRESETS, type QualityName } from './quality.js';
import { activeLook, sampleSky, type Look } from './look.js';
import { lightUniforms, patchShaderChunks, stylize } from './stylize.js';
import { FishingFx } from './fishing-fx.js';
import { CLUSTER_SPOTS, hasOreLook, oreClusterGeo, oreGlow, oreGlowAmt, oreRockGeo } from './ore-models.js';
import { CombatFx } from './combat-fx.js';
import { Bloom } from './post.js';
import { GlowShrooms, LightPool, buildLamps, wisps } from './lights.js';
import { buildStall, type Stall } from './stalls.js';
import { RainFront, Snowfall, snowAt } from './weather.js';
import { Traces } from './traces.js';
import { wallPiece } from './town-models.js';
import { buildPatch, buildPest, plotLook } from './farm-models.js';
import { TREE_PARTS } from './tree-models.js';

export { tileColors };
const clamp = (v: number, a: number, b: number) => (v < a ? a : v > b ? b : v);
function mulberry32(seed: number) { return () => { seed |= 0; seed = (seed + 0x6d2b79f5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }

export const LEAF_COLORS: Record<string, number[]> = { birch: [0xbcd96a, 0xd8e08a, 0xa8cb5b], pine: [0x3b7d52, 0x2f6b45], oak: [0x5a9a42, 0x8a9a3a, 0xa8822e], ironwood: [0x4fa6a2, 0x3a8c8c], willow: [0x9ab85a, 0xb8c86a], silverbark: [0xd8ecf0, 0xb8d4d8], yew: [0x2a4a2a, 0x3a6436], elder: [0x5a8a3a, 0xa8a040, 0xc8a040], ash: [0x8a9a5a, 0x9aaa6a], cedar: [0x2a5a4a, 0x407a60], ghostoak: [0xb8c0b0, 0xd0d6c8], stormwood: [0x3a4a40, 0x2a2624], blackwood: [0x1e2a24, 0x2a3a30], redwood: [0x2e5a3a, 0x8a3a22], glasswood: [0x9ae8e0, 0xc0fff4], ancientyew: [0x22402a, 0x325634], worldroot: [0x8a3a22, 0xc0602a] };
export const ORE_COL: Record<string, number> = { copper: 0xe0863c, tin: 0xd4dee3, iron: 0xb0523a, coal: 0x2a2a2e, silver: 0xe4e8ee, starsilver: 0xb9f2ff, gold: 0xf2c440, deepglass: 0x5ad8ff, emberheart: 0xff6a2a, cobalt: 0x3a5ad8, tungsten: 0x7a7a84, platinum: 0xf0f2f8, blackiron: 0x1a1a20, glowstone: 0x9af0c8, iridium: 0xc49cff };
/** Ores that shine on their own (and are seen from afar at night). */
const ORE_GLOW: Record<string, number> = { starsilver: 0x7fe8ff, deepglass: 0x5ad8ff, emberheart: 0xff7a30, glowstone: 0x7affc0, cobalt: 0x3a6aff };
/** Salvage is drawn as the thing it was: which builder for which kind. */
const SALVAGE_MODEL: Record<string, string> = { wreck: 'wreck_car', pylon: 'pylon', rails: 'rails', aircraft: 'aircraft', plating: 'plating', core: 'core' };
const FLECKS = [[0.32, 0.42, 0.14], [-0.26, 0.36, 0.3], [0.04, 0.5, -0.32]];
const HIDDEN = new THREE.Matrix4().compose(new THREE.Vector3(0, -200, 0), new THREE.Quaternion(), new THREE.Vector3(0.001, 0.001, 0.001));
const local = (y: number, s: [number, number, number] = [1, 1, 1], ox = 0, oz = 0) => new THREE.Matrix4().compose(new THREE.Vector3(ox, y, oz), new THREE.Quaternion(), new THREE.Vector3(...s));

/**
 * One resource's look. `k`, `sk` and `ri` index into its chunk's instanced meshes (its kind's
 * tree parts or ore flecks, the stumps, the rocks) and are set when the chunk is built.
 */
interface ResVisual { obj: MapObject; family: 'tree' | 'rock' | 'spot' | 'forage'; k: number; base: THREE.Matrix4; sk?: number; ri?: number; group?: THREE.Group; chunk?: number }
/** Tiles per chunk side. The world is drawn as a grid of these, only near the camera. */
const CHUNK = 32;
/** Chunks whose nearest edge is within this many tiles of the camera's target are built (the fog hides anything further)... */
const LOAD_R = 118;
/** ...and dropped again once they're this far (the gap stops chunks flickering in and out). */
const DROP_R = 160;
/** Grass, flowers and pebbles only grow on chunks this close: further out they're specks in the fog. */
const FOLIAGE_R = 56, FOLIAGE_DROP_R = 84;
interface Chunk {
  key: number; cx: number; cz: number;
  group: THREE.Group;
  terrain: THREE.Mesh;
  foliage: FoliageSet | null;
  trees: Record<string, THREE.InstancedMesh[]>;
  stump: THREE.InstancedMesh | null;
  /** One instanced mesh of rocks per ore (each ore has its own rock), and its ore clusters. */
  rocks: Record<string, THREE.InstancedMesh>;
  flecks: Record<string, THREE.InstancedMesh>;
  forage: THREE.Group[];
}
export interface Pick { obj?: MapObject; tile?: [number, number]; actor?: number; trap?: number; pile?: number }
interface Emitter { x: number; y: number; z: number; kind: 'chimney' | 'fire' | 'furnace' | 'beacon'; next: number }

/**
 * Everything 3D: sky, terrain, water, foliage, objects, particles, lighting and the camera.
 * Knows nothing about the network; the game feeds it state.
 */
export class WorldView {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  // the far plane sits just past the fog: nothing beyond it could be seen anyway
  readonly camera = new THREE.PerspectiveCamera(42, 1, 0.3, 150);
  readonly actorLayer = new THREE.Group();
  readonly particles: Particles;
  readonly look: Look = activeLook();
  yaw = 0.65; pitch = 0.88; dist = 21; yawVel = 0;
  readonly target = new THREE.Vector3();
  /** 0 = day, 1 = night. Read by the audio and the HUD. */
  night = 0;
  private hemi = new THREE.HemisphereLight(0xdfefff, 0x55603a, 1.4);
  private sun = new THREE.DirectionalLight(0xfff2d8, 2.8);
  private sky = new Sky();
  private water: Water;
  private glows: Glows;
  private birds: Birds;
  fishing!: FishingFx;
  combat!: CombatFx;
  private post: Bloom;
  private pool: LightPool;
  private shrooms: GlowShrooms;
  /** 0..1, smoothed toward the server's weather: how overcast it is. */
  private cloud = 0;
  private cloudTarget = 0;
  private clickables: THREE.Object3D[] = [];
  private res = new Map<number, ResVisual>();
  /** Chunks built right now, by key (cx + cz * 4096). */
  private chunks = new Map<number, Chunk>();
  /** Every tree, rock and forage patch, listed by chunk. */
  private chunkRes = new Map<number, MapObject[]>();
  private colors: number[][];
  private occ: Occluder[];
  private treeNear: Uint8Array;
  private foliageDensity = 1;
  /** Materials shared by every chunk (one shader program each). */
  private treeMats: Record<string, THREE.Material[]> = {};
  private rockMat = mat(0xffffff);
  private oreRockMat = mat(0xffffff, { vertexColors: true });
  private stumpMat = mat(0x7a5a3a);
  private fleckMats: Record<string, THREE.Material> = {};
  private stumpGeo = G.cyl(0.26, 0.32, 0.36, 7);
  private rockGeo = G.crag(0.62, 3);
  private fleckGeo = G.oct(0.15);
  private obstacleGroups = new Map<number, THREE.Group>();
  private patchGroups = new Map<number, THREE.Group>();
  private emitters: Emitter[] = [];
  private treeSpots: { x: number; y: number; z: number; kind: string }[] = [];
  private ambientClock = 0;
  private marker: THREE.Mesh;
  private markerT = 0;
  /** The ring round whatever you clicked to work (a rock, a tree, a spot); it stays while you work it. */
  private focusRing!: THREE.Mesh;
  private focus: { id: number; t: number; working: boolean } | null = null;
  /** Rocks jolt when a pick lands. */
  private jolt = new Map<number, number>();
  private frames = 0;
  /** The quest goal: a gold chevron bobbing over whatever the current step wants. */
  private goal: THREE.Group;
  private goalAt: [number, number, number] | null = null;
  private raycaster = new THREE.Raycaster();
  readonly allObstacles: Set<number>;
  private stalls: Stall[] = [];
  /** Trees shaking from a blow, or falling once felled: id → start time and lean direction. */
  private wobble = new Map<number, { t0: number; dx: number; dz: number; fall: boolean; amp: number; stay?: boolean; sinkAt?: number; thud?: boolean }>();
  private shakeAmt = 0;
  /** How worked each rock's vein looks: 0 fresh .. 3 nearly spent, and whether it's rich. */
  private veinLook = new Map<number, { stage: number; rich: boolean; glow?: THREE.Sprite }>();
  private glint: { sprite: THREE.Sprite; until: number; id: number } | null = null;
  private groundBirds: GroundBirds;
  private butterflies: Butterflies;
  private wildlife: Wildlife;
  private mistBanks: MistBanks;
  private mistK = 0;
  private rainFront: RainFront;
  private snowfall: Snowfall;
  readonly traces: Traces;
  private weatherNow: 'clear' | 'cloudy' | 'rain' = 'clear';
  private forecast: { next: string; at: number } | null = null;
  /** 0..1 how close incoming rain is (0 when none is coming). */
  rainComing = 0;
  /** Distant thunder as a storm closes in. */
  onThunder: ((pan: number, near: number) => void) | null = null;
  private nextThunder = 0;
  private flourishes: { g: THREE.Group; t: number }[] = [];
  private push = 0;
  private horizon = new THREE.Color();
  /** People on foot near the camera this frame (set by the game). */
  walkers: Walker[] = [];
  /** A flock of ground birds took off here. */
  onBirdsFlush: ((x: number, z: number) => void) | null = null;
  /** Resources currently used up (so a stray blow never redraws a felled tree). */
  private down = new Set<number>();
  private clock = 0;

  constructor(readonly canvas: HTMLCanvasElement, readonly map: GameMap, quality: QualityName) {
    patchShaderChunks();
    const q = PRESETS[quality];
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, q.pixelRatio));
    this.renderer.toneMapping = THREE.CustomToneMapping;
    this.renderer.toneMappingExposure = this.look.exposure;
    this.renderer.shadowMap.enabled = q.shadows;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.scene.fog = new THREE.Fog(0xa9cfe0, this.look.grade.fogNear, this.look.grade.fogFar);
    this.scene.add(this.hemi, this.sky.mesh);
    this.sun.castShadow = q.shadows;
    this.sun.shadow.mapSize.set(q.shadowSize, q.shadowSize);
    Object.assign(this.sun.shadow.camera, { left: -30, right: 30, top: 30, bottom: -30, near: 1, far: 150 });
    this.sun.shadow.bias = -0.0005; this.sun.shadow.normalBias = 0.03;
    this.scene.add(this.sun, this.sun.target, this.actorLayer);
    this.allObstacles = new Set(map.objects.filter(o => o.type === 'obstacle').map(o => o.id));

    this.post = new Bloom(this.renderer, 5, 4);
    this.post.enabled = q.bloom;
    this.pool = new LightPool(this.scene, q.lights);
    this.colors = tileColors(map);
    this.occ = this.occluders();
    this.treeNear = treeDensity(map);
    const scale = Math.max(1, map.width / 128);
    this.water = new Water(map, this.look, Math.min(320, Math.round((quality === 'low' ? 48 : 80) * scale)));
    this.scene.add(this.water.mesh);
    this.glows = new Glows(this.scene);
    this.buildResources();
    this.buildObjects();
    this.foliageDensity = q.foliage;
    // the sky dome must sit inside the far plane
    this.sky.mesh.scale.setScalar((this.camera.far * 0.85) / 300);
    this.particles = new Particles(this.scene);
    this.particles.budget = q.particles;
    this.combat = new CombatFx(this.scene, map, this.particles, this.glows);
    buildLamps(map, this.scene, this.glows, this.pool, this.lampSpots());
    this.shrooms = new GlowShrooms(map, this.scene, ['fen', 'grove'], q.shrooms);
    this.fishing = new FishingFx(this.scene, map, this.glows, this.particles, quality === 'low' ? 260 : quality === 'medium' ? 500 : 800);
    this.birds = new Birds(this.scene);
    this.groundBirds = new GroundBirds(this.scene, map, quality === 'low' ? 8 : 16);
    this.groundBirds.onFlush = (x, z) => this.onBirdsFlush?.(x, z);
    this.butterflies = new Butterflies(this.scene, map, quality === 'low' ? 6 : 12);
    this.wildlife = new Wildlife(this.scene, map, quality === 'low' ? 3 : 6);
    this.mistBanks = new MistBanks(this.scene, map, quality === 'low' ? 6 : 14);
    this.rainFront = new RainFront(this.scene);
    this.snowfall = new Snowfall(this.particles, map);
    this.traces = new Traces(this.scene, map, quality === 'low' ? 80 : 160);
    const ring = new THREE.RingGeometry(0.2, 0.32, 4); ring.rotateX(-Math.PI / 2);
    this.marker = new THREE.Mesh(ring, new THREE.MeshBasicMaterial({ color: 0xf1bf55, transparent: true, depthWrite: false }));
    this.marker.visible = false;
    this.scene.add(this.marker);
    const fr = new THREE.RingGeometry(0.66, 0.8, 40); fr.rotateX(-Math.PI / 2);
    this.focusRing = new THREE.Mesh(fr, new THREE.MeshBasicMaterial({ color: 0xf1bf55, transparent: true, opacity: 0, depthWrite: false, toneMapped: false }));
    this.focusRing.renderOrder = 4; this.focusRing.visible = false;
    this.scene.add(this.focusRing);
    this.makeGlint();
    this.goal = new THREE.Group();
    const gm = new THREE.MeshBasicMaterial({ color: 0xf1bf55, transparent: true, opacity: 0.92, depthTest: false });
    const chev = new THREE.Mesh(new THREE.ConeGeometry(0.32, 0.6, 4), gm); chev.rotation.x = Math.PI; chev.renderOrder = 10;
    const halo = new THREE.Mesh(new THREE.RingGeometry(0.42, 0.52, 24), new THREE.MeshBasicMaterial({ color: 0xf1bf55, transparent: true, opacity: 0.55, depthWrite: false, side: THREE.DoubleSide }));
    halo.rotation.x = -Math.PI / 2; halo.name = 'halo';
    this.goal.add(chev); this.goal.add(halo); this.goal.visible = false;
    this.scene.add(this.goal);
  }
  /** Point at the current quest goal (world x, z, and how high the thing is), or hide the pointer. */
  setGoal(at: [number, number, number] | null) { this.goalAt = at; this.goal.visible = !!at; }

  setQuality(name: QualityName) {
    const q = PRESETS[name];
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, q.pixelRatio));
    if (this.renderer.shadowMap.enabled !== q.shadows) {
      this.renderer.shadowMap.enabled = q.shadows; this.sun.castShadow = q.shadows;
      this.scene.traverse((o: THREE.Object3D) => { const m = (o as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined; if (m) (Array.isArray(m) ? m : [m]).forEach(x => { x.needsUpdate = true; }); });
    }
    if (this.sun.shadow.mapSize.x !== q.shadowSize) { this.sun.shadow.mapSize.set(q.shadowSize, q.shadowSize); this.sun.shadow.map?.dispose(); this.sun.shadow.map = null; }
    this.foliageDensity = q.foliage;
    for (const ch of this.chunks.values()) ch.foliage?.setDensity(q.foliage);
    this.particles.budget = q.particles;
    this.post.enabled = q.bloom;
    this.resize();
  }

  /* ---------------- build ---------------- */
  private occluders(): Occluder[] {
    const out: Occluder[] = [];
    for (const o of this.map.objects) {
      const cx = o.x + (o.w ?? 1) / 2, cz = o.z + (o.h ?? 1) / 2;
      if (o.type === 'resource') { const fam = RESOURCES[o.kind as ResourceKind]?.family; if (fam === 'tree') out.push({ x: cx, z: cz, r: o.kind === 'elder' ? 3.6 : 1.8, s: o.kind === 'elder' ? 0.45 : 0.32 }); else if (fam === 'rock') out.push({ x: cx, z: cz, r: 1.2, s: 0.25 }); }
      else if (o.type === 'house') out.push({ x: cx, z: cz, r: 2.6, s: 0.3 });
      else if (o.type === 'station' || o.type === 'landmark') out.push({ x: cx, z: cz, r: 1.3, s: 0.22 });
      else if (o.type === 'prop') out.push({ x: cx, z: cz, r: 1.7, s: 0.22 });
    }
    return out;
  }

  private baseMatrix(o: MapObject) {
    const x = o.x + 0.5, z = o.z + 0.5, r = hash2(o.id, 7) * Math.PI * 2, s = 0.85 + hash2(o.id, 13) * 0.3, sy = s * (0.85 + hash2(o.id, 29) * 0.4);
    return new THREE.Matrix4().compose(new THREE.Vector3(x, this.map.heightAt(x, z) - 0.05, z), new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), r), new THREE.Vector3(s, sy, s));
  }

  /** Work out every resource's place (its look is built later, with its chunk). */
  private buildResources() {
    const objs = this.map.objects.filter(o => o.type === 'resource' && o.kind in RESOURCES);
    const fam = (o: MapObject) => RESOURCES[o.kind as ResourceKind].family;
    for (const o of objs) {
      const f = fam(o);
      if (f === 'spot') { this.res.set(o.id, { obj: o, family: 'spot', k: 0, base: new THREE.Matrix4() }); continue; } // drawn by FishingFx
      if (f === 'tree' && !TREE_PARTS[o.kind]) continue;
      this.res.set(o.id, { obj: o, family: f as ResVisual['family'], k: 0, base: f === 'forage' ? new THREE.Matrix4() : this.baseMatrix(o) });
      const key = this.chunkKey(Math.floor(o.x / CHUNK), Math.floor(o.z / CHUNK));
      let list = this.chunkRes.get(key);
      if (!list) { list = []; this.chunkRes.set(key, list); }
      list.push(o);
      const y = this.map.heightAt(o.x + 0.5, o.z + 0.5);
      if (f === 'tree') {
        this.treeSpots.push({ x: o.x + 0.5, y: y + 2.4, z: o.z + 0.5, kind: o.kind });
        if (o.kind === 'silverbark') this.glows.add(o.x + 0.5, y + 2.8, o.z + 0.5, 0xcfeeff, 2.6, { base: 0, night: 0.55, flicker: 0.15 });
      }
      if (f === 'rock' && ORE_GLOW[o.kind]) this.glows.add(o.x + 0.5, y + 0.55, o.z + 0.5, ORE_GLOW[o.kind], 1.4, { base: 0.25, night: 0.5 });
    }
    for (const [kind, parts] of Object.entries(TREE_PARTS)) this.treeMats[kind] = parts.map(part => {
      const vertexColors = !!part.geo.getAttribute('color');
      if (!part.wind) return mat(part.vary ? 0xffffff : part.c, vertexColors ? { vertexColors } : undefined);
      const m = new THREE.MeshLambertMaterial({ color: 0xffffff, vertexColors }); addWind(m, part.wind, 1.2); stylize(m); return m;
    });
    for (const [kind, color] of Object.entries(ORE_COL)) this.fleckMats[kind] = hasOreLook(kind)
      ? mat(0xffffff, { vertexColors: true, ...(oreGlow(kind) !== undefined ? { emissive: oreGlow(kind), emissiveIntensity: oreGlowAmt(kind) } : {}) })
      : mat(color, ORE_GLOW[kind] ? { emissive: ORE_GLOW[kind], emissiveIntensity: 0.9 } : kind === 'coal' ? {} : { emissive: color, emissiveIntensity: 0.15 });
  }

  /* ---------------- chunks ---------------- */
  private chunkKey(cx: number, cz: number) { return cx + cz * 4096; }
  /** Build the chunks near (x, z) now, all of them (on arrival, or after a teleport). */
  warm(x: number, z: number) { this.updateChunks(x, z, Infinity); }
  /** Build missing chunks near (x, z), nearest first, within a time budget; drop far ones. */
  private updateChunks(x: number, z: number, budgetMs = 6) {
    const nx = Math.ceil(this.map.width / CHUNK), nz = Math.ceil(this.map.height / CHUNK);
    const edgeDist = (cx: number, cz: number) => {
      const dx = Math.max(cx * CHUNK - x, 0, x - (cx + 1) * CHUNK), dz = Math.max(cz * CHUNK - z, 0, z - (cz + 1) * CHUNK);
      return Math.hypot(dx, dz);
    };
    const t0 = performance.now();
    for (const ch of this.chunks.values()) {
      const d = edgeDist(ch.cx, ch.cz);
      if (d > DROP_R) { this.dropChunk(ch); continue; }
      if (ch.foliage && d > FOLIAGE_DROP_R) this.dropFoliage(ch);
    }
    // nearest first: grass on near chunks, then whole chunks further out
    const grow = [...this.chunks.values()].filter(ch => !ch.foliage && edgeDist(ch.cx, ch.cz) <= FOLIAGE_R).sort((a, b) => edgeDist(a.cx, a.cz) - edgeDist(b.cx, b.cz));
    for (const ch of grow) { this.growFoliage(ch); if (performance.now() - t0 > budgetMs) return; }
    const want: [number, number, number][] = [];
    const r = Math.ceil(LOAD_R / CHUNK) + 1, ccx = Math.floor(x / CHUNK), ccz = Math.floor(z / CHUNK);
    for (let cz = Math.max(0, ccz - r); cz <= Math.min(nz - 1, ccz + r); cz++) for (let cx = Math.max(0, ccx - r); cx <= Math.min(nx - 1, ccx + r); cx++) {
      if (this.chunks.has(this.chunkKey(cx, cz))) continue;
      const d = edgeDist(cx, cz);
      if (d <= LOAD_R) want.push([d, cx, cz]);
    }
    if (!want.length) return;
    want.sort((a, b) => a[0] - b[0]);
    for (const [d, cx, cz] of want) {
      const ch = this.buildChunk(cx, cz);
      if (d <= FOLIAGE_R) this.growFoliage(ch);
      if (performance.now() - t0 > budgetMs) break;
    }
  }
  private growFoliage(ch: Chunk) {
    const map = this.map, x0 = ch.cx * CHUNK, z0 = ch.cz * CHUNK, x1 = Math.min(map.width, x0 + CHUNK), z1 = Math.min(map.height, z0 + CHUNK);
    const t = performance.now();
    const occupied = (i: number) => map.objAt[i] >= 0 || map.obstacleAt[i] >= 0;
    ch.foliage = buildFoliage(map, this.look, this.colors, occupied, this.treeNear, x0, z0, x1, z1);
    for (const m of ch.foliage.meshes) ch.group.add(m);
    ch.foliage.setDensity(this.foliageDensity);
    this.buildStats.foliage += performance.now() - t;
  }
  private dropFoliage(ch: Chunk) {
    if (!ch.foliage) return;
    for (const m of ch.foliage.meshes) { ch.group.remove(m); m.dispose(); }
    ch.foliage = null;
  }
  /** Time spent building chunks, by part (ms), for the debug overlay. */
  readonly buildStats = { terrain: 0, foliage: 0, n: 0 };
  /** How many chunks are built (for tests and the debug overlay). */
  get chunkCount() { return this.chunks.size; }

  private buildChunk(cx: number, cz: number): Chunk {
    const map = this.map, key = this.chunkKey(cx, cz);
    const x0 = cx * CHUNK, z0 = cz * CHUNK, x1 = Math.min(map.width, x0 + CHUNK), z1 = Math.min(map.height, z0 + CHUNK);
    const group = new THREE.Group();
    const t0 = performance.now();
    const terrain = buildTerrain(map, this.colors, this.occ, x0, z0, x1, z1);
    group.add(terrain);
    this.buildStats.terrain += performance.now() - t0; this.buildStats.n++;
    const ch: Chunk = { key, cx, cz, group, terrain, foliage: null, trees: {}, stump: null, rocks: {}, flecks: {}, forage: [] };
    // a sphere around the chunk, for culling its instanced meshes as a whole
    const mx = (x0 + x1) / 2, mz = (z0 + z1) / 2;
    const sphere = new THREE.Sphere(new THREE.Vector3(mx, map.heightAt(Math.min(mx, map.width - 1), Math.min(mz, map.height - 1)), mz), Math.hypot(x1 - x0, z1 - z0) / 2 + 9);
    const inst = (geo: THREE.BufferGeometry, m: THREE.Material, n: number, objs: MapObject[], per = 1) => {
      const im = new THREE.InstancedMesh(geo, m, Math.max(1, n));
      im.count = n; im.castShadow = true; im.receiveShadow = true; im.frustumCulled = true; im.boundingSphere = sphere.clone();
      im.userData.objs = objs; if (per > 1) im.userData.per = per;
      group.add(im);
      return im;
    };
    const objs = this.chunkRes.get(key) ?? [];
    const isSalvage = (o: MapObject) => !!RESOURCES[o.kind as ResourceKind]?.salvage;
    const trees = objs.filter(o => this.res.get(o.id)?.family === 'tree'), rocks = objs.filter(o => this.res.get(o.id)?.family === 'rock' && !isSalvage(o));
    for (const [kind, parts] of Object.entries(TREE_PARTS)) {
      const list = trees.filter(o => o.kind === kind);
      if (!list.length) continue;
      list.forEach((o, k) => { const r = this.res.get(o.id)!; r.k = k; r.chunk = key; });
      ch.trees[kind] = parts.map((part, p) => {
        const im = inst(part.geo, this.treeMats[kind][p], list.length, list);
        if (part.vary) {
          const base = new THREE.Color(part.c);
          list.forEach((o, k) => { const v = 0.8 + hash2(o.x, o.z) * 0.32; const col = new THREE.Color(base.r * v, base.g * v * (0.95 + hash2(o.z, o.x) * 0.1), base.b * v); const sn = snowAt(map, o.x + 0.5, o.z + 0.5); if (sn > 0) col.lerp(new THREE.Color(0xeef2f6), Math.min(0.7, sn * 0.75) * (part.wind ?? 0.05) * 14); im.setColorAt(k, col); });
        }
        return im;
      });
    }
    if (trees.length) { ch.stump = inst(this.stumpGeo, this.stumpMat, trees.length, trees); trees.forEach((o, i) => { this.res.get(o.id)!.sk = i; }); }
    // rocks: each ore its own rock and its own cluster of ore, three clusters a rock
    for (const kind of [...new Set(rocks.map(o => o.kind))]) {
      const list = rocks.filter(o => o.kind === kind), own = hasOreLook(kind);
      const im = inst(own ? oreRockGeo(kind) : this.rockGeo, own ? this.oreRockMat : this.rockMat, list.length, list);
      ch.rocks[kind] = im;
      list.forEach((o, i) => {
        const r = this.res.get(o.id)!; r.ri = i; r.k = i; r.chunk = key;
        // no two rocks quite the same shade
        const v = own ? 0.88 + hash2(o.x * 3, o.z) * 0.24 : 0.3 + hash2(o.x * 3, o.z) * 0.1;
        im.setColorAt(i, new THREE.Color(v, v, v * (own ? 1 : 0.94)));
      });
      if (this.fleckMats[kind]) ch.flecks[kind] = inst(own ? oreClusterGeo(kind) : this.fleckGeo, this.fleckMats[kind], list.length * 3, list, 3);
    }
    // foraging patches: a few small plants each
    for (const o of objs.filter(o => this.res.get(o.id)?.family === 'forage')) {
      const g = new THREE.Group(), r = mulberry32(o.id * 97);
      for (let i = 0; i < 4; i++) {
        const x = (r() - 0.5) * 0.6, z = (r() - 0.5) * 0.6;
        if (o.kind === 'onions') { addMesh(g, G.ico(0.07), 0xd8b8d0, x, 0.06, z); for (let j = 0; j < 3; j++) { const l = addMesh(g, G.box(0.02, 0.3, 0.02), 0x5a9a3a, x + (j - 1) * 0.03, 0.2, z); l.rotation.z = (j - 1) * 0.25; } }
        else if (o.kind === 'mushrooms') { addMesh(g, G.cyl(0.025, 0.03, 0.12, 5), 0xe8dcc8, x, 0.06, z); addMesh(g, G.cone(0.09, 0.07, 7), 0xb85a3a, x, 0.15, z); }
        else if (o.kind === 'sand') {
          // a pale dune heaped by the tide, with a shell or two
          if (i === 0) { addMesh(g, G.sphere(0.5, 9), 0xe8d49a, 0, 0.02, 0).scale.set(1.1, 0.5, 0.9); addMesh(g, G.sphere(0.3, 8), 0xd8c088, 0.3, 0.02, 0.22).scale.set(1, 0.45, 1); const sp = addMesh(g, G.sharp(0.04, 0.6, 0.04), 0x7a5532, -0.1, 0.45, -0.05); sp.rotation.z = 0.35; addMesh(g, G.box(0.16, 0.2, 0.03), 0x8a8e92, -0.02, 0.2, -0.05).rotation.z = 0.35; }
          else addMesh(g, G.ico(0.035), [0xf0e8e0, 0xe8b8a8, 0xd8d0c0][i % 3], x * 0.8, 0.13, z * 0.8).scale.set(1, 0.5, 1);
        }
        else if (o.kind.startsWith('wild_')) {
          const h = HERBS[o.kind.slice(5) as HerbId], leaf = new THREE.Color(h?.color ?? '#5a8a4a').getHex();
          for (let j = 0; j < 3; j++) addMesh(g, G.sphere(0.07, 6), leaf, x + Math.cos(j * 2.1) * 0.06, 0.08 + j * 0.03, z + Math.sin(j * 2.1) * 0.06).scale.set(1.3, 0.4, 0.7);
          addMesh(g, G.ico(0.022), [0xf0f0e8, 0xe8c8e8, 0xf0e070][i % 3], x, 0.18, z);
        }
        else { addMesh(g, G.ico(0.11), 0x5a8a4a, x, 0.08, z).scale.set(1, 0.6, 1); addMesh(g, G.ico(0.025), 0xb090d8, x, 0.15, z); }
      }
      if (o.kind === 'mushrooms') g.userData.glowy = true;
      mergeStatic(g);
      const [px, py, pz] = this.objPos(o);
      g.position.set(px, py, pz);
      g.traverse((c: THREE.Object3D) => { c.userData.obj = o; });
      g.visible = !this.down.has(o.id);
      group.add(g); ch.forage.push(g);
      const rv = this.res.get(o.id)!; rv.group = g; rv.chunk = key;
    }
    // salvage: a car, a pylon, a stretch of rails... each its own model
    for (const o of objs.filter(o => this.res.get(o.id)?.family === 'rock' && isSalvage(o))) {
      const b = BUILDERS[SALVAGE_MODEL[o.kind] ?? 'wreck_car'];
      if (!b) continue;
      const g = new THREE.Group(); b(g); mergeStatic(g, m => flames.includes(m));
      const [px, py, pz] = this.objPos(o);
      g.position.set(px, py, pz);
      g.rotation.y = (Number(o.props?.facing ?? 0) % 4) * Math.PI / 2 + (hash2(o.id, 5) - 0.5) * 0.5;
      g.traverse((c: THREE.Object3D) => { c.userData.obj = o; if ((c as THREE.Mesh).isMesh) { c.castShadow = true; c.receiveShadow = true; } });
      group.add(g); ch.forage.push(g);
      const rv = this.res.get(o.id)!; rv.group = g; rv.chunk = key;
      this.drawSalvage(rv, this.down.has(o.id));
    }
    this.chunks.set(key, ch);
    this.scene.add(group); this.clickables.push(group);
    // show each resource as it is now (felled, worked, used up)
    for (const o of trees) this.drawTree(this.res.get(o.id)!);
    for (const o of rocks) this.drawRock(o.id, this.down.has(o.id));
    return ch;
  }

  private dropChunk(ch: Chunk) {
    this.scene.remove(ch.group);
    const i = this.clickables.indexOf(ch.group); if (i >= 0) this.clickables.splice(i, 1);
    ch.terrain.geometry.dispose();
    ch.group.traverse((o: THREE.Object3D) => { if ((o as THREE.InstancedMesh).isInstancedMesh) (o as THREE.InstancedMesh).dispose(); });
    for (const g of ch.forage) freeTree(g);
    for (const o of this.chunkRes.get(ch.key) ?? []) { const r = this.res.get(o.id); if (r) { r.chunk = undefined; r.group = undefined; } }
    this.chunks.delete(ch.key);
  }
  private chunkOf(r: ResVisual) { return r.chunk !== undefined ? this.chunks.get(r.chunk) : undefined; }

  /** Draw a tree as it is now: standing, a stump, or (while it falls or lies) left to updateWobble. */
  private drawTree(r: ResVisual) {
    const ch = this.chunkOf(r);
    if (!ch) return;
    const tmp = new THREE.Matrix4(), id = r.obj.id, w = this.wobble.get(id), depleted = this.down.has(id), gone = this.buildHidden.has(id);
    if (gone) { if (ch.stump && r.sk !== undefined) { ch.stump.setMatrixAt(r.sk, HIDDEN); ch.stump.instanceMatrix.needsUpdate = true; } (ch.trees[r.obj.kind] ?? []).forEach(im => { im.setMatrixAt(r.k, HIDDEN); im.instanceMatrix.needsUpdate = true; }); return; }
    if (ch.stump && r.sk !== undefined) { ch.stump.setMatrixAt(r.sk, depleted || w?.fall ? tmp.multiplyMatrices(r.base, local(0.15)) : HIDDEN); ch.stump.instanceMatrix.needsUpdate = true; }
    if (w) return; // shaking or falling: updateWobble draws it
    const parts = TREE_PARTS[r.obj.kind];
    (ch.trees[r.obj.kind] ?? []).forEach((im, p) => { im.setMatrixAt(r.k, depleted ? HIDDEN : tmp.multiplyMatrices(r.base, local(parts[p].y, parts[p].s))); im.instanceMatrix.needsUpdate = true; });
  }

  /** The server's forecast: what the weather turns to, in how many seconds. */
  setForecast(next: string, inS: number) { this.forecast = { next, at: this.clock + inS }; }
  setWeather(w: 'clear' | 'cloudy' | 'rain') { this.weatherNow = w; this.cloudTarget = w === 'rain' ? 1 : w === 'cloudy' ? 0.5 : 0; this.fishing.setWeather(w); }

  /** A resource ran out or came back. (A felled tree's fall is started by setFelled.) */
  setDepleted(id: number, depleted: boolean) {
    const r = this.res.get(id);
    if (!r) return;
    if (depleted) this.down.add(id); else this.down.delete(id);
    if (r.family === 'forage') { if (r.group) r.group.visible = !depleted; return; }
    if (r.family === 'tree') {
      const lying = this.wobble.get(id);
      // a felled trunk lying here was sawn up (or rotted): let it sink away
      if (depleted && lying?.fall) { lying.stay = false; lying.sinkAt = Math.max(this.clock, lying.t0 + 1.8); this.drawTree(r); return; }
      if (!depleted) this.wobble.delete(id); // grown back
      this.drawTree(r);
    } else if (r.family === 'rock') {
      if (!depleted) this.clearVein(id);
      if (r.group) this.drawSalvage(r, depleted);
      else this.drawRock(id, depleted);
    }
  }
  /** Stripped salvage sits lower and leans: there's nothing left on it worth taking. */
  private drawSalvage(r: { group?: THREE.Group; obj: MapObject }, depleted: boolean) {
    if (!r.group) return;
    const [, py] = this.objPos(r.obj);
    r.group.position.y = py - (depleted ? 0.18 : 0);
    r.group.rotation.z = depleted ? 0.06 : 0;
    r.group.scale.setScalar(depleted ? 0.94 : 1);
  }

  /** Rocks show how much of their vein is left: ore flecks wear away, the rock chips down. */
  private drawRock(id: number, depleted: boolean, jolt = 0) {
    const r = this.res.get(id);
    if (!r) return;
    const ch = this.chunkOf(r);
    const rockIm = ch?.rocks[r.obj.kind];
    if (!ch || !rockIm || r.ri === undefined) return;
    const tmp = new THREE.Matrix4(), look = this.veinLook.get(id), stage = look?.stage ?? 0;
    const im = ch.flecks[r.obj.kind];
    if (this.buildHidden.has(id)) { rockIm.setMatrixAt(r.ri, HIDDEN); rockIm.instanceMatrix.needsUpdate = true; if (im) { for (let j = 0; j < 3; j++) im.setMatrixAt(r.k * 3 + j, HIDDEN); im.instanceMatrix.needsUpdate = true; } return; }
    if (im) {
      const show = depleted ? 0 : Math.max(1, 3 - stage);
      if (hasOreLook(r.obj.kind)) {
        // the ore clusters sit on the rock's upper faces, leaning out, and shrink as the vein is worked
        const k = 1 - (depleted ? 3 : stage) * 0.06, sc = 1.55 * (1 - stage * 0.12);
        CLUSTER_SPOTS.forEach(([x, y, z, rx, rz], j) => im.setMatrixAt(r.k * 3 + j, j >= show ? HIDDEN : tmp.multiplyMatrices(r.base, new THREE.Matrix4().compose(new THREE.Vector3(x * 1.05 * k, 0.3 * k + y * 0.72 * k, z * 0.95 * k), new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, j * 2.1, rz)), new THREE.Vector3(sc, sc, sc)))));
      } else FLECKS.forEach((o, j) => im.setMatrixAt(r.k * 3 + j, j >= show ? HIDDEN : tmp.multiplyMatrices(r.base, local(o[1] * (1 - stage * 0.05), [1 - stage * 0.12, 1 - stage * 0.12, 1 - stage * 0.12], o[0], o[2]))));
      im.instanceMatrix.needsUpdate = true;
    }
    const k = 1 - (depleted ? 3 : stage) * 0.06, j = 1 - jolt;
    rockIm.setMatrixAt(r.ri, tmp.multiplyMatrices(r.base, local(0.3 * k * j, [1.05 * k * (1 + jolt * 0.5), 0.72 * k * j, 0.95 * k * (1 + jolt * 0.5)])));
    rockIm.instanceMatrix.needsUpdate = true;
  }
  private clearVein(id: number) { const l = this.veinLook.get(id); if (l?.glow) this.glows.remove(l.glow); this.veinLook.delete(id); }
  /** The server says how worked a rock's vein is. Rich veins shimmer so they're worth walking to. */
  setVein(id: number, stage: number, rich: boolean) {
    const r = this.res.get(id);
    if (!r || r.family !== 'rock') return;
    let l = this.veinLook.get(id);
    if (!l) { l = { stage: 0, rich: false }; this.veinLook.set(id, l); }
    l.stage = stage; l.rich = rich;
    if (rich && !l.glow) { const [x, y, z] = this.objPos(r.obj); l.glow = this.glows.add(x, y + 0.6, z, ORE_COL[r.obj.kind] ?? 0xffe0a0, 1.6, { base: 0.45, night: 0.3, flicker: 0.35 }); }
    if (l.glow) l.glow.visible = rich && !this.down.has(id);
    this.drawRock(id, this.down.has(id));
  }
  /** A weak point lights up on a rock: strike it while it shines. */
  showGlint(id: number, ms: number) {
    const r = this.res.get(id);
    if (!r) return;
    const [x, y, z] = this.objPos(r.obj);
    const g = this.makeGlint();
    g.id = id; g.until = this.clock + ms / 1000;
    g.sprite.position.set(x + (hash2(id, this.clock | 0) - 0.5) * 0.4, y + 0.55, z + 0.32);
    g.sprite.visible = true;
    this.particles.emit({ x, y: y + 0.6, z, count: 10, spread: 0.25, vy: 0.8, vSpread: 0.8, colors: [0xfff0b0, 0xffffff, ORE_COL[r.obj.kind] ?? 0xffd27a], size: 0.07, life: 0.6, gravity: 2 }, true);
  }
  /** Made up front, so its shader is ready before the first glint (a first-time compile is a visible hitch). */
  private makeGlint() {
    if (!this.glint) {
      const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.glows.tex, color: 0xfff0b0, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true, toneMapped: false }));
      sp.renderOrder = 5; sp.visible = false; this.scene.add(sp);
      this.glint = { sprite: sp, until: 0, id: -1 };
    }
    return this.glint;
  }
  clearGlint() { if (this.glint) this.glint.sprite.visible = false; }
  private updateGlint() {
    const g = this.glint;
    if (!g || !g.sprite.visible) return;
    if (this.clock > g.until) { g.sprite.visible = false; return; }
    const left = g.until - this.clock;
    g.sprite.scale.setScalar(0.55 + Math.sin(this.clock * 14) * 0.12 + Math.min(1, left) * 0.25);
    (g.sprite.material as THREE.SpriteMaterial).opacity = Math.min(1, left * 2);
  }

  /** A tree came down (dir is the way it fell). It lies there until it's sawn up. `animate` false: it was already lying when we arrived. */
  setFelled(id: number, dx: number, dz: number, animate: boolean) {
    const r = this.res.get(id);
    if (!r || r.family !== 'tree') return;
    const l = Math.hypot(dx, dz) || 1;
    this.wobble.set(id, { t0: animate ? this.clock : this.clock - 10, dx: dx / l, dz: dz / l, fall: true, amp: 0, stay: true, thud: !animate });
    this.drawTree(r);
  }
  isFelled(id: number) { const w = this.wobble.get(id); return !!w?.fall && !!w.stay; }

  /** An axe or pick landed on whatever is in front of (x, z): the tree shivers. */
  strike(x: number, z: number, face: number, kind: 'tree' | 'rock') {
    const fx = x + Math.sin(face), fz = z + Math.cos(face);
    let best: ResVisual | null = null, bd = 1.6;
    for (const r of this.res.values()) {
      if (r.family !== kind || this.down.has(r.obj.id)) continue;
      const [cx, , cz] = this.objPos(r.obj), d = Math.hypot(cx - fx, cz - fz);
      if (d < bd) { bd = d; best = r; }
    }
    if (!best) return null;
    if (kind === 'rock') { this.jolt.set(best.obj.id, this.clock); return best.obj; }
    if (this.wobble.get(best.obj.id)?.fall) return best.obj;
    this.wobble.set(best.obj.id, { t0: this.clock, dx: Math.sin(face), dz: Math.cos(face), fall: false, amp: 0.05 });
    return best.obj;
  }

  /**
   * You clicked something to work it: a ring snaps in round it, and stays (softer) while you work
   * it, so it's always clear what you're doing. The thing itself gives a little jump.
   */
  markObject(o: MapObject) {
    const [x, , z] = this.objPos(o), w = Math.max(o.w ?? 1, o.h ?? 1);
    this.focusRing.position.set(x, this.map.surfaceY(x, z, this.allObstacles) + 0.05, z);
    this.focusRing.userData.size = Math.max(1, w * 0.85);
    this.focusRing.visible = true;
    this.focus = { id: o.id, t: 0, working: false };
    const r = this.res.get(o.id);
    if (r?.family === 'rock') this.jolt.set(o.id, this.clock);
  }
  /** Busy (true) keeps the ring up; idle lets it fade. */
  setWorking(busy: boolean) { if (this.focus) this.focus.working = busy; }
  private updateFocus(dt: number) {
    const f = this.focus, ring = this.focusRing, m = ring.material as THREE.MeshBasicMaterial;
    if (!f) return;
    f.t += dt;
    const size = (ring.userData.size as number) ?? 1;
    // snap in from wide, then breathe gently while you work
    const pop = f.t < 0.22 ? 1.45 - (f.t / 0.22) * 0.45 : 1 + (f.working ? Math.sin(this.clock * 3) * 0.03 : 0);
    ring.scale.setScalar(size * pop);
    const fade = f.working || f.t < 1.2 ? 1 : Math.max(0, 1 - (f.t - 1.2) / 0.5);
    m.opacity = (f.t < 0.22 ? 0.95 : f.working ? 0.55 : 0.7) * fade;
    if (fade <= 0) { ring.visible = false; this.focus = null; }
  }
  private updateJolt() {
    for (const [id, t0] of this.jolt) {
      const t = this.clock - t0;
      this.drawRock(id, this.down.has(id), t < 0.25 ? Math.sin((t / 0.25) * Math.PI) * 0.06 : 0);
      if (t >= 0.25) this.jolt.delete(id);
    }
  }

  /**
   * A level-up: a column of golden light rises from the player, a ring flashes out over the
   * ground and sparks drift up. `self` also nudges the camera in for a moment.
   */
  flourish(x: number, z: number, self: boolean) {
    const y = Math.max(this.map.heightAt(x, z), 0), g = new THREE.Group();
    const col = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.75, 6, 20, 1, true), new THREE.MeshBasicMaterial({ color: 0xffd27a, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, toneMapped: false }));
    col.position.y = 3; g.add(col);
    const ring = new THREE.Mesh(new THREE.RingGeometry(0.4, 0.62, 32).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color: 0xfff0b0, transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false }));
    ring.position.y = 0.06; g.add(ring);
    g.position.set(x, y, z);
    this.scene.add(g);
    this.flourishes.push({ g, t: 0 });
    for (let i = 0; i < 3; i++) this.particles.emit({ x, y: y + 0.4 + i * 0.6, z, count: 10, spread: 0.5, vy: 1.6, vSpread: 0.8, colors: [0xffe6a0, 0xfff6d8, 0xffc060], size: 0.09, life: 1.6, gravity: -0.4, alpha: 1, wander: 0.4 }, true);
    if (self) this.push = 1;
  }

  /** Shake the camera a little (a heavy blow, a big hit taken). */
  shake(amount: number) { this.shakeAmt = Math.max(this.shakeAmt, amount); }

  /** Shaking and falling trees: rebuild their instance matrices each frame until they settle. */
  private updateWobble() {
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), axis = new THREE.Vector3(), rot = new THREE.Matrix4();
    for (const [id, w] of this.wobble) {
      const r = this.res.get(id)!, t = this.clock - w.t0, parts = TREE_PARTS[r.obj.kind], ch = this.chunkOf(r);
      let angle = 0, sink = 0, done = false;
      if (w.fall) {
        // creak, then accelerate over, a small bounce, lie a moment and sink away
        const k = Math.max(0, t - 0.15);
        angle = k < 1.0 ? 1.5 * Math.pow(k, 2.2) : 1.5 - Math.sin(Math.min(1, (k - 1.0) / 0.35) * Math.PI) * 0.08;
        if (w.stay) sink = 0;
        else if (w.sinkAt !== undefined) sink = Math.max(0, this.clock - w.sinkAt) * 1.2;
        else if (t > 3.2) sink = (t - 3.2) * 1.2;
        if (k >= 1.0 && !w.thud) { w.thud = true; const [cx, cy, cz] = this.objPos(r.obj); this.particles.dust(cx + w.dx * 1.6, cy + 0.2, cz + w.dz * 1.6, 0xa89a80); this.particles.leaves(cx + w.dx * 2.4, cy + 0.5, cz + w.dz * 2.4, LEAF_COLORS[r.obj.kind] ?? [0x6a9a42], 12); }
        done = w.stay ? false : w.sinkAt !== undefined ? this.clock - w.sinkAt > 1.3 : t > 4.2;
      } else {
        angle = Math.sin(t * 34) * w.amp * Math.exp(-t * 6);
        done = t > 0.7;
      }
      axis.set(w.dz, 0, -w.dx).normalize();
      q.setFromAxisAngle(axis, angle);
      rot.compose(new THREE.Vector3(0, -sink, 0), q, new THREE.Vector3(1, 1, 1));
      // rotate about the tree's own base: base · rot · part
      const baseNoRot = r.base;
      if (ch) (ch.trees[r.obj.kind] ?? []).forEach((im, p) => {
        if ((done && w.fall) || (!w.fall && this.down.has(id))) im.setMatrixAt(r.k, HIDDEN);
        else {
          const pos = new THREE.Vector3().setFromMatrixPosition(baseNoRot);
          m.makeTranslation(pos.x, pos.y, pos.z).multiply(rot).multiply(new THREE.Matrix4().makeTranslation(-pos.x, -pos.y, -pos.z)).multiply(baseNoRot).multiply(local(parts[p].y, parts[p].s));
          im.setMatrixAt(r.k, m);
        }
        im.instanceMatrix.needsUpdate = true;
      });
      if (done) { this.wobble.delete(id); this.drawTree(r); }
    }
  }

  /** World position of an object's centre, at ground height. */
  objPos(o: MapObject): [number, number, number] {
    const x = o.x + (o.w ?? 1) / 2, z = o.z + (o.h ?? 1) / 2;
    return [x, this.map.heightAt(x, z), z];
  }

  private place(o: MapObject, g: THREE.Group) {
    const [x, y, z] = this.objPos(o);
    g.position.set(x, y, z);
    g.traverse((c: THREE.Object3D) => { c.userData.obj = o; });
    this.scene.add(g); this.clickables.push(g);
  }

  /** Build mode: things the town layer will clear away when applied, hidden meanwhile so the preview is honest. */
  private buildHidden = new Set<number>();
  hideUnder(ids: Set<number>) {
    const was = this.buildHidden; this.buildHidden = ids;
    const touch = (id: number) => {
      const r = this.res.get(id);
      if (r) {
        if (r.group) r.group.visible = !ids.has(id) && !(r.family === 'forage' && this.down.has(id));
        else if (r.family === 'tree') this.drawTree(r);
        else if (r.family === 'rock') this.drawRock(id, this.down.has(id));
      }
      for (const g of this.clickables) if ((g.userData.obj as MapObject | undefined)?.id === id && !(g.userData.obj as MapObject).props?.town) g.visible = !ids.has(id);
    };
    for (const id of was) if (!ids.has(id)) touch(id);
    for (const id of ids) if (!was.has(id)) touch(id);
  }
  /** Build mode draws the town layer itself while you edit it: hide the placed town pieces meanwhile. */
  setTownVisible(v: boolean) { for (const g of this.clickables) if ((g.userData.obj as MapObject | undefined)?.props?.town) g.visible = v; }

  /** Windows and lamps glow at night, stovepipes smoke: points a town-kit builder left in userData. */
  private townLights(g: THREE.Group, at: (g: THREE.Group, x: number, y: number, z: number) => THREE.Vector3) {
    for (const [x, y, z] of (g.userData.lights ?? []) as [number, number, number][]) { const c = at(g, x, y, z); this.glows.add(c.x, c.y, c.z, 0xffc46a, 1.2, { base: 0, night: 0.85, flicker: 0.1 }); }
    const ls = (g.userData.lights ?? []) as [number, number, number][];
    if (ls.length) { const c = at(g, ls[0][0], ls[0][1], ls[0][2] + 0.4); this.pool.add({ x: c.x, y: c.y, z: c.z, color: 0xffb860, intensity: 1.3, range: 5, flicker: 0.1, night: 1 }); }
    for (const [x, y, z] of (g.userData.smoke ?? []) as [number, number, number][]) { const c = at(g, x, y, z); this.emitters.push({ ...c, kind: 'chimney', next: Math.random() }); }
  }

  private buildObjects() {
    const at = (g: THREE.Group, lx: number, ly: number, lz: number) => new THREE.Vector3(lx, ly, lz).applyMatrix4(g.matrixWorld);
    for (const o of this.map.objects) {
      if (o.type === 'station' || o.type === 'landmark' || o.type === 'house') {
        const b = BUILDERS[o.kind]; if (!b) continue;
        const g = new THREE.Group(); b(g);
        mergeStatic(g, m => flames.includes(m));
        if (o.type === 'house' || o.kind === 'ferry' || o.props?.town) g.rotation.y = (Number(o.props?.facing ?? 0) % 4) * Math.PI / 2;
        if (o.props?.town && o.type !== 'station') g.scale.set(...pieceScale(o.kind));
        this.place(o, g);
        g.updateMatrixWorld(true);
        this.townLights(g, at);
        if (o.kind === 'cottage') {
          const c = at(g, -0.7, 3.1, -0.5); this.emitters.push({ ...c, kind: 'chimney', next: Math.random() });
          const w = at(g, 0.75, 0.95, 1.4); this.glows.add(w.x, w.y, w.z, 0xffc46a, 1.3, { base: 0, night: 0.85, flicker: 0.1 });
          const wl = at(g, 0.75, 1.0, 2.0); this.pool.add({ x: wl.x, y: wl.y, z: wl.z, color: 0xffb860, intensity: 1.3, range: 5, flicker: 0.1, night: 1 });
        }
        if (o.kind === 'campfire') { const c = at(g, 0, 0.5, 0); this.emitters.push({ ...c, kind: 'fire', next: 0 }); this.glows.add(c.x, c.y + 0.1, c.z, 0xff9a40, 3.4, { base: 0.45, night: 0.5, flicker: 0.6 }); this.pool.add({ x: c.x, y: c.y + 0.6, z: c.z, color: 0xff8a30, intensity: 3.2, range: 9, flicker: 0.7, night: 0.35 }); }
        if (o.kind === 'furnace') { const c = at(g, 0.25, 2.2, -0.25); this.emitters.push({ ...c, kind: 'furnace', next: 0 }); const m = at(g, 0, 0.42, 0.7); this.glows.add(m.x, m.y, m.z, 0xff7a20, 1.8, { base: 0.4, night: 0.5, flicker: 0.4 }); this.pool.add({ x: m.x, y: m.y + 0.3, z: m.z + 0.4, color: 0xff6a20, intensity: 2.6, range: 7, flicker: 0.5, night: 0.4 }); }
        if (o.kind === 'range') { const m = at(g, 0, 0.62, 0.5); this.glows.add(m.x, m.y, m.z, 0xff7a30, 1.2, { base: 0.35, night: 0.5, flicker: 0.4 }); this.pool.add({ x: m.x, y: m.y, z: m.z + 0.3, color: 0xff7030, intensity: 1.8, range: 5, flicker: 0.5, night: 0.5 }); }
        if (o.kind === 'beacon') { const c = at(g, 0, 3.3, 0); this.emitters.push({ ...c, kind: 'beacon', next: 0 }); this.glows.add(c.x, c.y, c.z, 0xff9a40, 4, { base: 0.5, night: 0.5, flicker: 0.6 }); this.pool.add({ x: c.x, y: c.y, z: c.z, color: 0xff8a30, intensity: 5, range: 14, flicker: 0.6, night: 0.3 }); }
        if (o.kind === 'lantern') { const c = at(g, 0.5, 1.88, 0); this.glows.add(c.x, c.y, c.z, 0xffd27a, 2.4, { base: 0.25, night: 0.75, flicker: 0.15 }); this.pool.add({ x: c.x, y: c.y, z: c.z, color: 0xffc070, intensity: 2.8, range: 9, flicker: 0.15, night: 0.9 }); }
      } else if (o.type === 'bridge') { const d = this.bridgeDeck(o, false); mergeStatic(d); this.scene.add(d); }
      else if ((o.type === 'prop' || o.type === 'decor') && o.kind !== 'stall' && BUILDERS[o.kind]) {
        const g = new THREE.Group(), joined = typeof o.props?.join === 'number' && isWall(o.kind);
        if (joined) wallPiece(g, o.kind, Number(o.props!.join), Number(o.props?.facing ?? 0), o.x, o.z);
        else BUILDERS[o.kind](g);
        mergeStatic(g, m => flames.includes(m));
        // placed town pieces sit square (walls that join are drawn the right way already); scattered clutter gets a little twist
        g.rotation.y = joined ? 0 : (Number(o.props?.facing ?? Math.floor(hash2(o.id, 3) * 4)) % 4) * Math.PI / 2 + (o.kind === 'signpost' || o.props?.town ? 0 : (hash2(o.id, 5) - 0.5) * 0.5);
        if (o.props?.town) g.scale.set(...pieceScale(o.kind));
        g.traverse((c: THREE.Object3D) => { if ((c as THREE.Mesh).isMesh) { c.castShadow = true; c.receiveShadow = true; } });
        this.place(o, g);
        if (o.props?.town) { g.updateMatrixWorld(true); this.townLights(g, at); }
        if (o.kind === 'brazier') { g.updateMatrixWorld(true); const c = at(g, 0, 1.3, 0); this.emitters.push({ ...c, kind: 'fire', next: 0 }); this.glows.add(c.x, c.y, c.z, 0xff9a40, 2.4, { base: 0.4, night: 0.5, flicker: 0.6 }); this.pool.add({ x: c.x, y: c.y, z: c.z, color: 0xff8a30, intensity: 2.4, range: 8, flicker: 0.6, night: 0.4 }); }
      }
      else if (o.type === 'prop' && o.kind === 'stall') {
        const st = buildStall(String(o.props?.style ?? 'general'));
        mergeStatic(st.group, m => within(m, st.shutter));
        st.group.rotation.y = (Number(o.props?.facing ?? 0) % 4) * Math.PI / 2;
        this.place(o, st.group);
        st.group.updateMatrixWorld(true);
        const c = at(st.group, st.lamp.x, st.lamp.y, st.lamp.z);
        this.glows.add(c.x, c.y, c.z, 0xffc070, 1.4, { base: 0.05, night: 0.85, flicker: 0.12 });
        this.pool.add({ x: c.x, y: c.y, z: c.z, color: 0xffb060, intensity: 1.8, range: 6, flicker: 0.15, night: 1 });
        this.stalls.push(st);
      }
    }
  }

  private bridgeDeck(o: MapObject, broken: boolean) {
    const g = new THREE.Group();
    const x0 = o.x, x1 = o.x + (o.w ?? 1), z0 = o.z, z1 = o.z + (o.h ?? 1);
    const r = mulberry32(x0 * 31 + z0);
    // planks run across the way you walk: a deck longer north-south (a jetty) is drawn turned
    const alongZ = z1 - z0 > x1 - x0;
    const [a0, a1, b0, b1] = alongZ ? [z0, z1, x0, x1] : [x0, x1, z0, z1];
    const at = (a: number, b: number, y: number, la: number, lb: number, h: number, c: number) => addMesh(g, alongZ ? G.box(lb, h, la) : G.box(la, h, lb), c, alongZ ? b : a, y, alongZ ? a : b);
    for (let a = a0 - 0.3; a < a1 + 0.3; a += 0.34) {
      if (broken && r() < 0.55 && a > a0 + 0.5 && a < a1 - 0.5) continue;
      at(a + 0.17, (b0 + b1) / 2, 0.42, 0.28, b1 - b0 + 0.2, 0.1, r() < 0.5 ? 0x7a5a38 : 0x6c4e30);
    }
    for (const b of [b0 - 0.05, b1 + 0.05]) {
      for (let a = a0; a <= a1; a += Math.max(1, (a1 - a0) / 4)) at(a, b, 0.2, 0.14, 0.14, 1.4, 0x5a3d24);
      if (!broken) at((a0 + a1) / 2, b, 0.85, a1 - a0 + 0.2, 0.08, 0.08, 0x6c4e30);
    }
    return g;
  }

  /** Obstacles look different depending on whether *this* player has cleared them. */
  setCleared(cleared: ReadonlySet<number>) {
    for (const o of this.map.objects) {
      if (o.type !== 'obstacle') continue;
      const done = cleared.has(o.id), old = this.obstacleGroups.get(o.id);
      if (old && old.userData.done === done) continue;
      if (old) { this.scene.remove(old); freeTree(old); this.clickables.splice(this.clickables.indexOf(old), 1); }
      const g = new THREE.Group(); g.userData.done = done;
      const r = mulberry32(o.id * 97), tiles = this.map.objTiles(o);
      if (o.kind === 'rockslide' && !done) for (const t of tiles) for (let k = 0; k < 2; k++) { const x = this.map.tx(t) + 0.2 + r() * 0.6, z = this.map.tz(t) + 0.2 + r() * 0.6, s = 0.45 + r() * 0.45; addMesh(g, G.dod(s), r() < 0.5 ? 0x948d82 : 0x847a6f, x, this.map.heightAt(x, z) + s * 0.5, z).rotation.set(r() * 3, r() * 3, 0); }
      if (o.kind === 'bramble' && !done) for (const t of tiles) for (let k = 0; k < 2; k++) {
        const x = this.map.tx(t) + 0.2 + r() * 0.6, z = this.map.tz(t) + 0.2 + r() * 0.6, s = 0.5 + r() * 0.35, y = this.map.heightAt(x, z);
        addMesh(g, G.ico(s), r() < 0.5 ? 0x3e4a32 : 0x553c50, x, y + s * 0.7, z).rotation.set(r() * 3, r() * 3, 0);
        for (let j = 0; j < 3; j++) addMesh(g, G.cone(0.05, 0.4, 4), 0xd2c294, x + (r() - 0.5) * s * 1.6, y + s * (0.5 + r()), z + (r() - 0.5) * s * 1.6).rotation.set(r() * 3, 0, r() * 3);
      }
      if (o.kind === 'bridge') g.add(this.bridgeDeck(o, !done));
      if (o.kind === 'stones') for (const t of tiles) { if (this.map.tz(t) !== o.z) continue; addMesh(g, G.cyl(0.38, 0.45, 0.6, 7), 0x8a9591, this.map.tx(t) + 0.5, 0.05, o.z + 1 + (r() - 0.5) * 0.4); }
      mergeStatic(g);
      g.traverse((c: THREE.Object3D) => { c.userData.obj = o; });
      this.scene.add(g); this.clickables.push(g); this.obstacleGroups.set(o.id, g);
    }
  }

  /** Farming patches: each player sees their own crop. Only patches whose look changed are rebuilt. */
  setPlots(plots: Record<string, PlotState>, now: number) {
    for (const o of this.map.objects) {
      if (o.type !== 'patch') continue;
      const ps = plots[plotKey(o)], v = plotView(ps, now), key = plotLook(v, !!ps?.scarecrow), old = this.patchGroups.get(o.id);
      if (old && old.userData.look === key) continue;
      if (old) { this.scene.remove(old); freeTree(old); this.clickables.splice(this.clickables.indexOf(old), 1); }
      const g = buildPatch(o, this.map, v, !!ps?.scarecrow);
      mergeStatic(g);
      g.userData.look = key;
      g.traverse((c: THREE.Object3D) => { c.userData.obj = o; });
      this.scene.add(g); this.clickables.push(g); this.patchGroups.set(o.id, g);
    }
  }

  /** A crow or rabbit at one of your patches (ms 0: it's gone). */
  private pests = new Map<number, { g: THREE.Group; until: number; seed: number; flee: number }>();
  showPest(o: MapObject, critter: string, ms: number) {
    const old = this.pests.get(o.id);
    if (!ms) { if (old) old.flee = this.clock; return; }
    if (old) { this.scene.remove(old.g); freeTree(old.g); this.clickables.splice(this.clickables.indexOf(old.g), 1); }
    const g = buildPest(critter), seed = Math.random();
    const x = o.x + 0.6 + seed * ((o.w ?? 1) - 1.2), z = o.z + 0.5 + ((seed * 7) % 1) * ((o.h ?? 1) - 1);
    // on top of the crop, a little larger than life so it reads from the usual camera height
    g.position.set(x, this.map.heightAt(x, z) + 0.34, z);
    g.scale.setScalar(1.7);
    g.rotation.y = seed * Math.PI * 2;
    g.traverse((c: THREE.Object3D) => { c.userData.obj = o; });
    this.scene.add(g); this.clickables.push(g);
    this.pests.set(o.id, { g, until: this.clock + ms / 1000, seed, flee: 0 });
  }
  private updatePests() {
    for (const [id, p] of this.pests) {
      const bob = p.g.userData.bob as THREE.Object3D, t = this.clock * 6 + p.seed * 10;
      if (p.flee || this.clock > p.until + 0.5) {
        // off it goes: a crow flies up and away, a rabbit bolts
        const f = this.clock - (p.flee || p.until);
        p.g.position.y += 0.06; p.g.translateZ(0.08);
        if (f > 1.2) { this.scene.remove(p.g); freeTree(p.g); this.clickables.splice(this.clickables.indexOf(p.g), 1); this.pests.delete(id); }
        continue;
      }
      bob.rotation.x = Math.max(0, Math.sin(t)) * 0.6;
      bob.position.y = Math.abs(Math.sin(t * 0.5)) * 0.03;
    }
  }

  /* ---------------- input ---------------- */
  pick(clientX: number, clientY: number): Pick | null {
    const r = this.canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2(((clientX - r.left) / r.width) * 2 - 1, -((clientY - r.top) / r.height) * 2 + 1);
    this.raycaster.setFromCamera(ndc, this.camera);
    const o = this.raycaster.ray.origin, d = this.raycaster.ray.direction, m = this.map;
    let tile: [number, number] | undefined, gDist = Infinity;
    for (let s = 0; s < 260; s += 0.2) {
      const x = o.x + d.x * s, y = o.y + d.y * s, z = o.z + d.z * s;
      if (x >= 0 && z >= 0 && x < m.width && z < m.height && y <= Math.max(m.surfaceY(x, z, this.allObstacles), 0)) { tile = [Math.floor(x), Math.floor(z)]; gDist = s; break; }
      if (y < -3) break;
    }
    for (const h of this.raycaster.intersectObjects([this.actorLayer, ...this.fishing.buoys, ...this.combat.piles.values(), ...this.clickables], true)) {
      if (h.distance > gDist + 1.2) break;
      // your own character never gets in the way of what's behind or beside it
      if (h.object.userData.self) continue;
      if (h.object.userData.actorId !== undefined) return { actor: h.object.userData.actorId as number, tile };
      if (h.object.userData.trap !== undefined) return { trap: h.object.userData.trap as number, tile };
      if (h.object.userData.pile !== undefined) return { pile: h.object.userData.pile as number, tile };
      let obj: MapObject | undefined;
      if ((h.object as THREE.InstancedMesh).isInstancedMesh) { const list = h.object.userData.objs as MapObject[]; obj = list?.[Math.floor((h.instanceId ?? 0) / (h.object.userData.per ?? 1))]; }
      else obj = h.object.userData.obj as MapObject | undefined;
      if (obj) return { obj, tile };
    }
    // missed everything by a little? Take the nearest creature or person within a thumb's width on screen
    {
      let best: number | undefined, bd = (matchMedia('(pointer: coarse)').matches ? 30 : 20) ** 2;
      const v = new THREE.Vector3();
      for (const root of this.actorLayer.children) {
        const proxy = root.children.find((c: THREE.Object3D) => c.userData.hitProxy);
        if (!proxy || root.userData.self || !root.visible || !root.userData.mob || (root.userData.view as { deadAt?: number } | undefined)?.deadAt) continue;
        proxy.getWorldPosition(v); v.project(this.camera);
        if (v.z > 1) continue;
        const sx = r.left + (v.x + 1) / 2 * r.width, sy = r.top + (1 - v.y) / 2 * r.height, d = (sx - clientX) ** 2 + (sy - clientY) ** 2;
        if (d < bd) { bd = d; best = root.userData.actorId as number; }
      }
      if (best !== undefined) return { actor: best, tile };
    }
    if (tile) {
      const t = m.idx(tile[0], tile[1]);
      const id = m.objAt[t] >= 0 ? m.objAt[t] : m.obstacleAt[t];
      const obj = id >= 0 ? m.objById.get(id) : undefined;
      if (obj && obj.type === 'resource' && obj.props?.hidden && !this.fishing.visible(obj.id)) return { tile };
      if (obj) return { obj, tile };
      return { tile };
    }
    return null;
  }

  showMarker(x: number, z: number) { this.marker.position.set(x, this.map.surfaceY(x, z, this.allObstacles) + 0.06, z); this.marker.visible = true; this.markerT = 0.8; }

  resize() {
    const w = this.canvas.clientWidth, h = this.canvas.clientHeight;
    this.renderer.setSize(w, h, false);
    const db = this.renderer.getDrawingBufferSize(new THREE.Vector2());
    this.post?.setSize(db.x, db.y);
    this.camera.aspect = w / Math.max(1, h); this.camera.updateProjectionMatrix();
    this.particles?.resize(h * this.renderer.getPixelRatio(), this.camera.fov);
  }

  /* ---------------- per frame ---------------- */
  /** Classic: the overhead view. Close: low and over the shoulder, near your character. */
  camMode: 'classic' | 'close' = 'classic';
  get camLimits(): { dist: [number, number]; pitch: [number, number] } { return this.camMode === 'close' ? { dist: [3.5, 12], pitch: [0.08, 0.8] } : { dist: [9, 42], pitch: [0.42, 1.32] }; }
  setCamMode(m: 'classic' | 'close') {
    if (m === this.camMode) return;
    this.camMode = m;
    if (m === 'close') { this.dist = 6.5; this.pitch = 0.3; } else { this.dist = 18; this.pitch = 0.8; }
  }
  private lookAtV = new THREE.Vector3();
  frame(dt: number, time: number, dayT: number, focus: THREE.Vector3) {
    this.yaw += this.yawVel * dt;
    // a long jump (a teleport, waking after death): cut straight there and build the ground first
    if (Math.hypot(focus.x - this.target.x, focus.z - this.target.z) > 30) { this.target.copy(focus); this.warm(focus.x, focus.z); }
    this.target.lerp(focus, Math.min(1, dt * 8));
    // a level-up nudges the camera in and back out
    this.push = Math.max(0, this.push - dt / 1.4);
    const dd = this.dist * (1 - 0.1 * Math.sin(this.push * Math.PI));
    const cx = this.target.x + Math.sin(this.yaw) * Math.cos(this.pitch) * dd, cz = this.target.z + Math.cos(this.yaw) * Math.cos(this.pitch) * dd;
    const close = this.camMode === 'close';
    // close: aim at head height, a little to the right of you, so you see past your shoulder
    const lift = close ? 1.35 : 0, side = close ? 0.38 : 0;
    const rx = Math.cos(this.yaw) * side, rz = -Math.sin(this.yaw) * side;
    const cy = Math.max(this.target.y + lift + Math.sin(this.pitch) * dd, this.map.heightAt(clamp(cx + rx, 0, this.map.width), clamp(cz + rz, 0, this.map.height)) + (close ? 0.5 : 1.2));
    this.clock = time;
    this.lookAtV.set(this.target.x + rx, this.target.y + lift, this.target.z + rz);
    this.camera.position.set(cx + rx, cy, cz + rz); this.camera.lookAt(this.lookAtV);
    if (this.shakeAmt > 0.002) {
      const a = this.shakeAmt;
      this.camera.position.add(new THREE.Vector3((Math.random() - 0.5) * a, (Math.random() - 0.5) * a, (Math.random() - 0.5) * a));
      this.shakeAmt *= Math.exp(-dt * 9);
    }
    this.updateChunks(this.target.x, this.target.z);
    this.updateWobble();
    this.updateGlint();
    this.updatePests();
    for (let i = this.flourishes.length - 1; i >= 0; i--) {
      const f = this.flourishes[i]; f.t += dt;
      const [col, ring] = f.g.children as THREE.Mesh[], k = f.t / 1.6;
      (col.material as THREE.MeshBasicMaterial).opacity = Math.sin(Math.min(1, k) * Math.PI) * 0.45;
      col.scale.set(1 - k * 0.5, 0.4 + k * 0.9, 1 - k * 0.5);
      ring.scale.setScalar(1 + k * 5); (ring.material as THREE.MeshBasicMaterial).opacity = Math.max(0, 0.9 - k * 1.4);
      if (k >= 1) { this.scene.remove(f.g); freeTree(f.g); this.flourishes.splice(i, 1); }
    }
    // shutters: open by day, down at night
    for (const st of this.stalls) {
      const want = this.night > 0.55 ? 0 : 1;
      st.open += (want - st.open) * Math.min(1, dt * 1.5);
      // by day the shutter lies folded back on the awning; at dusk it swings down over the counter
      st.shutter.rotation.x = -4.4 * st.open;
      st.shutter.visible = st.open < 0.97;
    }
    this.sky.mesh.position.copy(this.camera.position);
    wind.uTime.value = time;
    // the breeze picks up with the weather and comes in gusts; its direction wanders slowly
    const wa = 0.45 + Math.sin(time * 0.013) * 0.5;
    wind.uWindDir.value.set(Math.cos(wa), Math.sin(wa));
    wind.uWind.value = 1 + this.cloud * 0.7;
    wind.uGust.value = (0.25 + this.cloud * 0.65) * (0.55 + 0.45 * Math.max(0, Math.sin(time * 0.11) * Math.sin(time * 0.067 + 1.3) + 0.3));
    const near = this.walkers.filter(w => Math.abs(w.x - this.target.x) < 20 && Math.abs(w.z - this.target.z) < 20).slice(0, PUSHERS);
    wind.uPush.value.forEach((v, i) => { const w = near[i]; if (w) v.set(w.x, w.z, w.moving ? 0.95 : 0.7, w.moving ? 1 : 0.6); else v.set(0, 0, 0, 0); });
    lightUniforms.uHMTime.value = time;
    this.cloud += (this.cloudTarget - this.cloud) * Math.min(1, dt * 0.2);
    this.lighting(dayT, time);
    this.water.uniforms.uTime.value = time;
    this.fishing.update(dt, time, this.target.x, this.target.z, this.night);
    this.combat.update(dt, time * 1000);
    if (this.goalAt) {
      const [gx, gz, gh] = this.goalAt, y = this.map.surfaceY(gx, gz, this.allObstacles);
      this.goal.position.set(gx, y, gz);
      this.goal.children[0].position.y = gh + 0.7 + Math.sin(time * 3) * 0.15;
      this.goal.children[0].rotation.y = time * 1.5;
      const halo = this.goal.children[1]; halo.position.y = 0.05; halo.scale.setScalar(1 + (time % 1.4) * 0.5);
      ((halo as THREE.Mesh).material as THREE.MeshBasicMaterial).opacity = 0.55 * (1 - (time % 1.4) / 1.4);
    }
    if (this.markerT > 0) { this.markerT -= dt; (this.marker.material as THREE.MeshBasicMaterial).opacity = clamp(this.markerT / 0.8, 0, 1); this.marker.rotation.y += dt * 3; if (this.markerT <= 0) this.marker.visible = false; }
    this.ambient(dt, time);
    this.particles.update(dt, time);
    this.glows.update(time, this.night);
    this.birds.update(dt, time, this.target.x, this.target.z, 1 - this.night);
    this.groundBirds.update(dt, time, this.target.x, this.target.z, (1 - this.night) * (1 - this.fishing.wet), this.walkers);
    this.butterflies.update(time, this.target.x, this.target.z, 1 - this.night, this.fishing.wet);
    this.wildlife.update(dt, time, this.target.x, this.target.z, this.night, this.walkers);
    {
      // rain you can see coming (and going)
      const left = this.forecast ? this.forecast.at - this.clock : 999;
      const raining = this.weatherNow === 'rain';
      const approach = !raining && this.forecast?.next === 'rain' ? Math.max(0, Math.min(1, 1 - left / 120)) : 0;
      const leaving = raining && this.forecast && this.forecast.next !== 'rain' ? Math.max(0, Math.min(1, 1 - left / 60)) : 0;
      this.rainComing = approach;
      if (approach > 0) {
        this.cloudTarget = Math.max(this.cloudTarget, 0.5 * approach);
        wind.uGust.value = Math.max(wind.uGust.value, 0.3 + approach * 0.6);
        if (approach > 0.35 && time > this.nextThunder) { this.nextThunder = time + 9 + Math.random() * 14; this.onThunder?.(Math.random() * 1.6 - 0.8, approach); }
      }
      this.rainFront.update(dt, time, this.camera, this.target.x, this.target.z, approach, raining, leaving, wind.uWindDir.value, this.horizon);
      this.snowfall.update(dt, this.target.x, this.target.z, wind.uWindDir.value, this.cloud);
      this.traces.update(dt);
    }
    this.mistBanks.update(dt, this.target.x, this.target.z, this.mistK, this.horizon, wind.uWindDir.value, this.fishing.wet);
    this.pool.update(time, this.target.x, this.target.z, this.night, this.night > 0.1 ? focus : null);
    this.shrooms.update(this.night, time);
    this.updateFocus(dt);
    this.updateJolt();
    if (++this.frames === 3) this.prewarm();
    this.post.render(this.scene, this.camera);
  }

  /**
   * Compile every shader the game makes on demand (glints, halos, level-up light, effects that
   * start hidden) once, up front. Otherwise the first glint or level-up stalls a frame while the
   * GPU driver compiles it: the "lag" you'd feel the first time you mine or level.
   */
  prewarm() {
    const samples = new THREE.Group();
    samples.add(new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.5, 1, 6, 1, true), new THREE.MeshBasicMaterial({ color: 0xffd27a, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, toneMapped: false })));
    samples.add(new THREE.Mesh(new THREE.RingGeometry(0.4, 0.6, 8), new THREE.MeshBasicMaterial({ color: 0xfff0b0, transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false })));
    samples.position.copy(this.target);
    this.scene.add(samples);
    const hidden: THREE.Object3D[] = [];
    this.scene.traverse((o: THREE.Object3D) => { if (!o.visible) { hidden.push(o); o.visible = true; } });
    try { this.post.compile(this.scene, this.camera); } catch { /* best effort */ }
    for (const o of hidden) o.visible = false;
    this.scene.remove(samples);
    samples.traverse((o: THREE.Object3D) => { if (o instanceof THREE.Mesh) { o.geometry.dispose(); (o.material as THREE.Material).dispose(); } });
  }

  /** Lamp posts by each cottage door and the village's work spots. */
  private lampSpots(): [number, number][] {
    const m = this.map, out: [number, number][] = [];
    const free = (x: number, z: number) => m.inBounds(x, z) && m.walkable(m.idx(x, z)) && m.objAt[m.idx(x, z)] < 0 && !out.some(([a, b]) => Math.abs(a - x) + Math.abs(b - z) < 3);
    const near = (x: number, z: number) => { for (let r = 0; r <= 3; r++) for (let dz = -r; dz <= r; dz++) for (let dx = -r; dx <= r; dx++) if (Math.max(Math.abs(dx), Math.abs(dz)) === r && free(x + dx, z + dz)) return [x + dx, z + dz] as [number, number]; return null; };
    const dirs: [number, number][] = [[0, 2], [2, 0], [0, -2], [-2, 0]];
    for (const o of m.objects) {
      if (o.props?.town) continue; // placed towns light themselves (street lamps, windows)
      let p: [number, number] | null = null;
      if (o.type === 'house') { const f = Number(o.props?.facing ?? 0) % 4; p = near(o.x + 1 + dirs[f][0] + (dirs[f][1] ? 1 : 0), o.z + 1 + dirs[f][1] + (dirs[f][0] ? 1 : 0)); }
      else if (o.type === 'station' && ['merchant', 'anvil', 'chest', 'workbench'].includes(o.kind) && !o.props?.name?.toString().startsWith('Ridge')) p = near(o.x + 1, o.z + 1);
      else if (o.type === 'landmark' && o.kind === 'well') p = near(o.x + 2, o.z);
      if (p) out.push(p);
    }
    return out;
  }

  /** Chimney smoke, campfire embers, fireflies and falling leaves near the player. */
  private ambient(dt: number, time: number) {
    const tx = this.target.x, tz = this.target.z, p = this.particles;
    for (const e of this.emitters) {
      if (Math.abs(e.x - tx) > 40 || Math.abs(e.z - tz) > 40) continue;
      e.next -= dt;
      if (e.next > 0) continue;
      if (e.kind === 'chimney') { p.smoke(e.x, e.y, e.z); e.next = 0.45 + Math.random() * 0.3; }
      else if (e.kind === 'furnace') { p.smoke(e.x, e.y, e.z, true); e.next = 0.35 + Math.random() * 0.2; }
      else { p.embers(e.x, e.y, e.z); if (Math.random() < 0.35) p.smoke(e.x, e.y + 0.4, e.z, e.kind === 'beacon'); e.next = 0.12 + Math.random() * 0.12; }
    }
    this.ambientClock -= dt;
    if (this.ambientClock > 0) return;
    this.ambientClock = 0.25;
    wisps(p, this.map, tx, tz, this.night);
    if (this.night > 0.5 && Math.random() < 0.7 * this.night) {
      const x = tx + (Math.random() - 0.5) * 22, z = tz + (Math.random() - 0.5) * 22;
      if (this.map.inBounds(Math.floor(x), Math.floor(z))) p.firefly(x, this.map.heightAt(x, z) + 0.6 + Math.random() * 0.8, z);
    }
    if (Math.random() < 0.35) {
      const near = this.treeSpots.filter(s => Math.abs(s.x - tx) < 14 && Math.abs(s.z - tz) < 14);
      const s = near[Math.floor(Math.random() * near.length)];
      if (s && s.kind !== 'pine') p.leaves(s.x, s.y, s.z, LEAF_COLORS[s.kind], 1);
    }
    void time;
  }

  private lighting(t: number, time: number) {
    const k = sampleSky(this.look, t);
    this.night = k.night;
    this.mistK = k.mist;
    const top = new THREE.Color(k.top), horizon = new THREE.Color(k.horizon);
    // overcast: grey the sky, flatten the light, pull the fog in
    const oc = this.cloud * (1 - k.night * 0.6);
    if (oc > 0.001) { top.lerp(new THREE.Color(0x7c8a96).multiplyScalar(1 - k.night * 0.8), oc * 0.6); horizon.lerp(new THREE.Color(0xa2acb2).multiplyScalar(1 - k.night * 0.8), oc * 0.55); }
    const fog = this.scene.fog as THREE.Fog;
    const G = this.look.grade;
    fog.near = G.fogNear * (1 - this.fishing.wet * 0.44); fog.far = G.fogFar - this.fishing.wet * 45;
    this.sky.uniforms.uTop.value.copy(top); this.sky.uniforms.uHorizon.value.copy(horizon);
    (this.scene.fog as THREE.Fog).color.copy(horizon);
    this.horizon.copy(horizon).lerp(new THREE.Color(0xffffff), 0.35 * (1 - k.night)).multiplyScalar(1 - k.night * 0.45);
    const az = t * Math.PI * 2 * 0.5 + 0.6;
    const elev = (k.elev * Math.PI) / 180;
    const sunDir = new THREE.Vector3(Math.cos(az) * Math.cos(elev), Math.sin(elev), Math.sin(az) * Math.cos(elev)).normalize();
    const moonDir = new THREE.Vector3(-Math.cos(az) * 0.6, 0.7, -Math.sin(az) * 0.6 + 0.3).normalize();
    this.sky.uniforms.uSunDir.value.copy(sunDir);
    this.sky.uniforms.uMoonDir.value.copy(moonDir);
    this.sky.uniforms.uSunColor.value.set(k.light);
    this.sky.uniforms.uNight.value = k.night;
    this.sky.uniforms.uTime.value = time;
    const lightDir = k.elev > 2 ? sunDir : k.elev > -6 ? sunDir.clone().lerp(moonDir, (2 - k.elev) / 8).normalize() : moonDir;
    // keep the key light at least ~25° up so low suns rake across the land without blacking it out
    if (lightDir.y < 0.42) { lightDir.y = 0.42; lightDir.normalize(); }
    this.sun.position.copy(this.target).addScaledVector(lightDir.normalize(), 60);
    this.sun.target.position.copy(this.target);
    this.sun.color.set(k.light); this.sun.intensity = k.lightI * (1 - 0.55 * this.cloud);
    this.hemi.color.set(k.hemiSky); this.hemi.groundColor.set(k.hemiGround); this.hemi.intensity = k.hemiI * (1 - 0.12 * this.cloud);
    const L = lightUniforms;
    L.uHMRim.value.set(k.hemiSky).multiplyScalar((0.28 + k.night * 0.25) * G.rim);
    L.uHMSun.value.set(k.light).multiplyScalar(k.lightI * 0.45);
    L.uHMCloud.value = (this.look.clouds + this.cloud * 0.35) * (1 - k.night);
    L.uHMMist.value = k.mist;
    const w = this.water.uniforms;
    w.uSkyTop.value.copy(top); w.uSkyHorizon.value.copy(horizon); w.uSunDir.value.copy(lightDir); w.uSunColor.value.set(k.light).multiplyScalar(k.elev > 0 ? 1.1 : 0.55);
    w.uFogColor.value.copy(horizon); w.uNight.value = k.night; w.uFogNear.value = fog.near; w.uFogFar.value = fog.far;
    for (const f of flames) f.scale.y = 0.85 + Math.sin(time * 13 + f.id) * 0.12 + Math.random() * 0.06;
  }
}
